import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  Input,
  Button,
  Tag,
  Spin,
  Empty,
  Row,
  Col,
  Select,
  Divider,
  Radio
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  ShoppingOutlined,
  ShopOutlined,
  CheckOutlined
} from "@ant-design/icons";
import ProductServ from "../service/ProductServ";
import SafeImage, { getImageUrl } from "./SafeImage";

const { Option } = Select;

const CuratedProductPickerModal = ({
  open,
  onClose,
  selectedProductIds = [],
  linkedCategoryIds = [],
  categories = [],
  onSave,
}) => {
  const [loading, setLoading] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("all");
  const [filterTab, setFilterTab] = useState("all"); // 'all' | 'added'

  // Local selection state inside the picker
  const [tempSelectedIds, setTempSelectedIds] = useState([]);
  const [selectedProductMap, setSelectedProductMap] = useState(new Map());

  // Initialize temp selection when modal opens
  useEffect(() => {
    if (open) {
      setTempSelectedIds((selectedProductIds || []).map(String));
      setSearchText("");
      setSelectedCategoryFilter("all");
      setFilterTab("all");
      fetchProducts();
    }
  }, [open, selectedProductIds]);

  // Fetch products from backend
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await ProductServ.index({ limit: 500 });
      const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setAllProducts(list);

      // Cache product objects for the selected ones
      const map = new Map();
      list.forEach(p => map.set(String(p._id || p.id), p));
      setSelectedProductMap(map);
    } catch (err) {
      console.error("Failed to load products for picker:", err);
    } finally {
      setLoading(false);
    }
  };

  // Convert linked category IDs to a normalized set of strings
  const linkedCategorySet = useMemo(() => {
    return new Set((linkedCategoryIds || []).map(String));
  }, [linkedCategoryIds]);

  // Filter products: MUST match one of the selected linked categories (or if none selected, show all), PLUS any already selected products
  const categoryFilteredProducts = useMemo(() => {
    const selectedSet = new Set((tempSelectedIds || []).map(String));
    if (linkedCategorySet.size === 0) {
      return allProducts;
    }

    return allProducts.filter(p => {
      const idStr = String(p._id || p.id);
      if (selectedSet.has(idStr)) return true; // Always include existing added products
      const catId = p.categoryId?._id || p.categoryId || p.category?._id || p.category;
      return catId && linkedCategorySet.has(String(catId));
    });
  }, [allProducts, linkedCategorySet, tempSelectedIds]);

  // Additional search, tab filter, and sorting: EXISTING ADDED ITEMS FIRST
  const displayedProducts = useMemo(() => {
    const selectedSet = new Set((tempSelectedIds || []).map(String));

    const filtered = categoryFilteredProducts.filter(p => {
      const idStr = String(p._id || p.id);
      const isAdded = selectedSet.has(idStr);

      // Added-only tab filter
      if (filterTab === "added" && !isAdded) {
        return false;
      }

      // Search text match
      if (searchText.trim()) {
        const query = searchText.toLowerCase().trim();
        const nameMatch = (p.name || "").toLowerCase().includes(query);
        const shopMatch = (p.shopId?.name || p.shop?.name || "").toLowerCase().includes(query);
        if (!nameMatch && !shopMatch) return false;
      }

      // Category dropdown filter
      if (selectedCategoryFilter !== "all") {
        const catId = String(p.categoryId?._id || p.categoryId || p.category?._id || p.category);
        if (catId !== selectedCategoryFilter && !isAdded) return false;
      }

      return true;
    });

    // Sort: Existing Added Products ALWAYS display FIRST at the top!
    return [...filtered].sort((a, b) => {
      const aSelected = selectedSet.has(String(a._id || a.id));
      const bSelected = selectedSet.has(String(b._id || b.id));
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });
  }, [categoryFilteredProducts, searchText, selectedCategoryFilter, tempSelectedIds, filterTab]);

  // Toggle selection for a product
  const toggleProduct = (prodId, prod) => {
    const idStr = String(prodId);
    setTempSelectedIds(prev => {
      if (prev.includes(idStr)) {
        return prev.filter(id => id !== idStr);
      } else {
        return [...prev, idStr];
      }
    });

    if (prod) {
      setSelectedProductMap(prev => {
        const newMap = new Map(prev);
        newMap.set(idStr, prod);
        return newMap;
      });
    }
  };

  // Select all shown
  const handleSelectAllShown = () => {
    const shownIds = displayedProducts.map(p => String(p._id || p.id));
    setTempSelectedIds(prev => {
      const set = new Set([...prev, ...shownIds]);
      return Array.from(set);
    });
  };

  // Clear selection
  const handleClearSelection = () => {
    setTempSelectedIds([]);
  };

  // Confirm selection
  const handleConfirm = () => {
    const selectedObjs = tempSelectedIds.map(id => selectedProductMap.get(id)).filter(Boolean);
    if (onSave) {
      onSave(tempSelectedIds, selectedObjs);
    }
    if (onClose) {
      onClose();
    }
  };

  // Get names of linked categories for the subtitle header
  const linkedCategoryNames = useMemo(() => {
    return categories
      .filter(c => linkedCategorySet.has(String(c._id || c.id)))
      .map(c => c.name);
  }, [categories, linkedCategorySet]);

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 0" }}>
          <div style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: "linear-gradient(135deg, #0055FF 0%, #16A34A 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: 18
          }}>
            <ShoppingOutlined />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#1e293b" }}>
              Pick Curated Products for Collection
            </h3>
            <p style={{ margin: 0, fontSize: 12, color: "#64748b" }}>
              {linkedCategoryNames.length > 0 ? (
                <span>
                  Filtering products from selected categories: <strong>{linkedCategoryNames.join(", ")}</strong>
                </span>
              ) : (
                <span>Showing all available store products</span>
              )}
            </p>
          </div>
        </div>
      }
      open={open}
      onCancel={onClose}
      width={1000}
      footer={null}
      destroyOnClose
      centered
      bodyStyle={{ maxHeight: "80vh", overflowY: "auto", padding: "16px 24px" }}
    >
      {/* Top Filter and Search Bar */}
      <div style={{ background: "#f8fafc", padding: 14, borderRadius: 12, border: "1px solid #e2e8f0", marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={8} md={8}>
            <Input
              placeholder="Search by product name or shop..."
              prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              size="middle"
              style={{ borderRadius: 8 }}
            />
          </Col>

          {linkedCategoryNames.length > 1 && (
            <Col xs={12} sm={6} md={5}>
              <Select
                value={selectedCategoryFilter}
                onChange={setSelectedCategoryFilter}
                style={{ width: "100%" }}
                size="middle"
              >
                <Option value="all">All Linked Categories</Option>
                {categories
                  .filter(c => linkedCategorySet.has(String(c._id || c.id)))
                  .map(c => (
                    <Option key={String(c._id || c.id)} value={String(c._id || c.id)}>
                      {c.name}
                    </Option>
                  ))}
              </Select>
            </Col>
          )}

          <Col xs={12} sm={linkedCategoryNames.length > 1 ? 4 : 8} md={5}>
            <Radio.Group
              value={filterTab}
              onChange={(e) => setFilterTab(e.target.value)}
              size="middle"
              buttonStyle="solid"
              style={{ width: "100%", display: "flex" }}
            >
              <Radio.Button value="all" style={{ flex: 1, textAlign: "center", fontSize: 12, borderRadius: "6px 0 0 6px" }}>
                All ({categoryFilteredProducts.length})
              </Radio.Button>
              <Radio.Button value="added" style={{ flex: 1, textAlign: "center", fontSize: 12, borderRadius: "0 6px 6px 0", color: tempSelectedIds.length > 0 ? "#16a34a" : undefined, fontWeight: tempSelectedIds.length > 0 ? 700 : 400 }}>
                ✓ Added ({tempSelectedIds.length})
              </Radio.Button>
            </Radio.Group>
          </Col>

          <Col xs={24} sm={6} md={6} style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button size="small" onClick={handleSelectAllShown} style={{ borderRadius: 6 }}>
              Select All ({displayedProducts.length})
            </Button>
            {tempSelectedIds.length > 0 && (
              <Button size="small" danger onClick={handleClearSelection} style={{ borderRadius: 6 }}>
                Clear ({tempSelectedIds.length})
              </Button>
            )}
          </Col>
        </Row>
      </div>

      {/* Product List / Cards Grid */}
      {loading ? (
        <div style={{ padding: "60px 0", textAlign: "center" }}>
          <Spin size="large" tip="Loading category products..." />
        </div>
      ) : displayedProducts.length === 0 ? (
        <Empty
          description={
            <div>
              <div style={{ fontWeight: 600, color: "#475569", marginBottom: 4 }}>
                No products found matching these categories
              </div>
              <div style={{ fontSize: 12, color: "#94a3b8" }}>
                Make sure products are added under {linkedCategoryNames.join(" or ") || "the selected categories"}.
              </div>
            </div>
          }
          style={{ padding: "40px 0" }}
        />
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 14,
          maxHeight: "52vh",
          overflowY: "auto",
          padding: "4px"
        }}>
          {displayedProducts.map(prod => {
            const prodId = String(prod._id || prod.id);
            const isAdded = tempSelectedIds.includes(prodId);
            const image = prod.imageUrl || prod.images?.[0];
            const price = prod.price || prod.mrp || 0;
            const mrp = prod.mrp || prod.price || 0;
            const discount = mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;
            const shopName = prod.shopId?.name || prod.shop?.name;
            const categoryName = prod.categoryId?.name || prod.category?.name;

            return (
              <div
                key={prodId}
                onClick={() => toggleProduct(prodId, prod)}
                style={{
                  display: "flex",
                  gap: 12,
                  padding: 12,
                  borderRadius: 12,
                  border: isAdded ? "2px solid #22c55e" : "1.5px solid #e2e8f0",
                  background: isAdded ? "#f0fdf4" : "#ffffff",
                  boxShadow: isAdded ? "0 4px 12px rgba(34, 197, 94, 0.12)" : "0 2px 6px rgba(0,0,0,0.03)",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  position: "relative",
                  overflow: "hidden"
                }}
              >
                {/* Selection Check Indicator */}
                {isAdded && (
                  <div style={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    background: "#22c55e",
                    color: "#fff",
                    borderRadius: "50%",
                    width: 20,
                    height: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)"
                  }}>
                    <CheckOutlined />
                  </div>
                )}

                {/* Product Thumbnail */}
                <div style={{
                  width: 70,
                  height: 70,
                  borderRadius: 8,
                  overflow: "hidden",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  flexShrink: 0
                }}>
                  <SafeImage
                    src={getImageUrl(image)}
                    alt={prod.name}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </div>

                {/* Product Info Details */}
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  <div>
                    <h5 style={{
                      margin: "0 0 2px 0",
                      fontSize: 13,
                      fontWeight: 700,
                      color: isAdded ? "#14532d" : "#0f172a",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      paddingRight: isAdded ? 18 : 0
                    }}>
                      {prod.name}
                    </h5>

                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
                      {categoryName && (
                        <Tag color="blue" style={{ fontSize: 10, borderRadius: 4, margin: 0, padding: "0 4px" }}>
                          {categoryName}
                        </Tag>
                      )}
                      {shopName && (
                        <span style={{ fontSize: 11, color: "#64748b", display: "flex", alignItems: "center", gap: 2 }}>
                          <ShopOutlined style={{ fontSize: 10 }} /> {shopName}
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                      <span style={{ fontSize: 14, fontWeight: 800, color: "#0055FF" }}>
                        ₹{price}
                      </span>
                      {discount > 0 && (
                        <>
                          <span style={{ fontSize: 11, color: "#94a3b8", textDecoration: "line-through" }}>
                            ₹{mrp}
                          </span>
                          <span style={{ fontSize: 10, fontWeight: 700, color: "#16a34a" }}>
                            {discount}% OFF
                          </span>
                        </>
                      )}
                    </div>

                    {/* Action Button: Disabled/Added look vs Add button */}
                    {isAdded ? (
                      <Tag
                        color="success"
                        style={{
                          fontWeight: 700,
                          fontSize: 11,
                          borderRadius: 6,
                          margin: 0,
                          padding: "2px 8px",
                          display: "flex",
                          alignItems: "center",
                          gap: 4
                        }}
                      >
                        ✓ Added
                      </Tag>
                    ) : (
                      <Button
                        size="small"
                        type="primary"
                        icon={<PlusOutlined />}
                        style={{
                          borderRadius: 6,
                          fontSize: 11,
                          height: 24,
                          background: "#0055FF",
                          fontWeight: 600
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleProduct(prodId, prod);
                        }}
                      >
                        Add
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Divider style={{ margin: "16px 0" }} />

      {/* Footer Controls */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Tag color={tempSelectedIds.length > 0 ? "blue" : "default"} style={{ fontWeight: 700, fontSize: 13, padding: "4px 10px", borderRadius: 8 }}>
            📦 {tempSelectedIds.length} Products Selected
          </Tag>
          <span style={{ fontSize: 12, color: "#64748b" }}>
            Click any product card to toggle add/remove
          </span>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <Button onClick={onClose} style={{ borderRadius: 8 }}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleConfirm}
            style={{
              background: "linear-gradient(135deg, #0055FF 0%, #16A34A 100%)",
              borderColor: "#0055FF",
              fontWeight: 700,
              borderRadius: 8,
              padding: "0 22px"
            }}
          >
            Confirm & Attach ({tempSelectedIds.length})
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CuratedProductPickerModal;
